import Link from 'next/link';
import parse from 'html-react-parser';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import Tags from '@/components/static/Tags';
import Tag from '@/components/static/Tag';
import Button from '@/components/static/Button';
import { PostProps, TermProps } from '@/lib/types';
import dateFormat from 'dateformat';

export default function FirstPost({ post, translations, locale }: { post: PostProps; translations: any; locale: string }) {
    const startDate = new Date(post.acf?.event?.startDate || post.date);

    const endDate = post.acf?.event?.endDate
        ? new Date(post.acf.event.endDate).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
          })
        : false;

    return (
        <Link
            href={post.link ?? ''}
            className={cn('relative grid grid-cols-1 bg-[#EAECEF] rounded-[10px] overflow-hidden mb-10 lg:mb-20 lg:grid-cols-2 w-full')}
        >
            <div className="relative flex justify-center items-center h-full w-full overflow-hidden">
                {post._embedded?.['wp:term']?.[0] && (
                    <Tags className="absolute left-0 top-0 p-3 z-10">
                        {post._embedded['wp:term'][0]?.map((term: TermProps, i: number) => (
                            <Tag key={i} label={term.name} />
                        ))}
                    </Tags>
                )}
                {post._embedded?.['wp:featuredmedia']?.[0] && (
                    <Image
                        src={post._embedded['wp:featuredmedia'][0].source_url ?? ''}
                        alt={post._embedded['wp:featuredmedia'][0].alt_text ?? ''}
                        width={post._embedded['wp:featuredmedia'][0].media_details.width ?? ''}
                        height={post._embedded['wp:featuredmedia'][0].media_details.height ?? ''}
                        className={cn('w-full h-full object-fit', post.acf.type == 28 ? 'object-contain object-center mx-auto' : 'object-cover')}
                    />
                )}
            </div>
            <div className="p-6 lg:p-12 flex flex-col justify-center">
                <div className="inline-block mb-4">
                    <PostDate
                        className="text-white text-sm bg-brand-light rounded-[8px] px-2 py-1 inline-block w-auto"
                        start={startDate}
                        end={endDate}
                        location={post.acf?.event?.location}
                        country={post.acf?.event?.country}
                    />
                </div>
                <h3 className="text-h5 font-medium leading-[140%] hover:text-brand transition">{post.title.rendered}</h3>
                <div className="text-textSecondary mt-6">{parse(post.excerpt.rendered)}</div>
                <Button as="button" type="text" className="mt-6 text-brand-light">
                    {translations.readArticle[locale]}
                </Button>
            </div>
        </Link>
    );
}

const PostDate = ({
    start,
    end,
    location,
    country,
    className,
}: {
    start: Date;
    end?: string | boolean;
    location?: string;
    country?: string;
    className?: string;
}) => {
    return (
        <div className={cn('text-base text-brand-light', className)}>
            <span>{dateFormat(start, 'd. mmmm, yyyy')}</span>
            {end && <span> - </span>}
            {end && <span>{end}</span>}
            {(location || country) && <span> | </span>}
            <span>{location}</span>
            {location && country && ', '}
            <span>{country}</span>
        </div>
    );
};
